import { useMemo } from 'react';
import type { ExternalNetworkEntityKind } from '@terra/shared-contracts-external-network';
import type { ChatMentionsMenuItemType } from '@terra/ui-design-system';
import {
  buildExternalAssetMentionParentItem,
  EXTERNAL_ASSET_MENTION_KINDS,
} from '../../utils/externalAssetMentionUtils';
import { useExternalNetworkAssets } from './useSurfaceAssets';

type UseExternalAssetMentionCategoriesParams = {
  surfaceId: string | undefined;
  enabled?: boolean;
};

type ExternalAssetRecord = Record<string, unknown> & { id: string };

const EMPTY_EXTERNAL_ASSET_MENTION_CATEGORIES: ChatMentionsMenuItemType[] = [];

function toExternalAssetRecords(data: unknown[]): ExternalAssetRecord[] {
  return data.flatMap((item) => {
    if (typeof item !== 'object' || item === null) {
      return [];
    }

    const record = item as Record<string, unknown>;

    if (typeof record.id !== 'string') {
      return [];
    }

    return [{ ...record, id: record.id }];
  });
}

function useExternalAssetKindAssets({
  surfaceId,
  entityKind,
  enabled,
}: {
  surfaceId: string | undefined;
  entityKind: ExternalNetworkEntityKind;
  enabled: boolean;
}) {
  return useExternalNetworkAssets({
    surfaceId: surfaceId ?? null,
    entityKind,
    enabled: Boolean(surfaceId) && enabled,
  });
}

export function useExternalAssetMentionCategories({
  surfaceId,
  enabled = true,
}: UseExternalAssetMentionCategoriesParams): ChatMentionsMenuItemType[] {
  const domains = useExternalAssetKindAssets({
    surfaceId,
    entityKind: 'DOMAIN',
    enabled,
  });
  const hosts = useExternalAssetKindAssets({
    surfaceId,
    entityKind: 'HOST',
    enabled,
  });
  const gateways = useExternalAssetKindAssets({
    surfaceId,
    entityKind: 'GATEWAY',
    enabled,
  });
  const services = useExternalAssetKindAssets({
    surfaceId,
    entityKind: 'NETWORK_SERVICE',
    enabled,
  });
  const netblocks = useExternalAssetKindAssets({
    surfaceId,
    entityKind: 'NETBLOCK',
    enabled,
  });
  const software = useExternalAssetKindAssets({
    surfaceId,
    entityKind: 'SOFTWARE',
    enabled,
  });
  const storageServices = useExternalAssetKindAssets({
    surfaceId,
    entityKind: 'STORAGE_SERVICE',
    enabled,
  });

  return useMemo(() => {
    if (!surfaceId || !enabled) {
      return EMPTY_EXTERNAL_ASSET_MENTION_CATEGORIES;
    }

    const assetsByKind: Record<
      ExternalNetworkEntityKind,
      ExternalAssetRecord[]
    > = {
      DOMAIN: toExternalAssetRecords(domains.data),
      HOST: toExternalAssetRecords(hosts.data),
      GATEWAY: toExternalAssetRecords(gateways.data),
      NETWORK_SERVICE: toExternalAssetRecords(services.data),
      NETBLOCK: toExternalAssetRecords(netblocks.data),
      SOFTWARE: toExternalAssetRecords(software.data),
      STORAGE_SERVICE: toExternalAssetRecords(storageServices.data),
    };

    return EXTERNAL_ASSET_MENTION_KINDS.map((entityKind) =>
      buildExternalAssetMentionParentItem({
        entityKind,
        surfaceId,
        assets: assetsByKind[entityKind],
      }),
    );
  }, [
    surfaceId,
    enabled,
    domains.data,
    hosts.data,
    gateways.data,
    services.data,
    netblocks.data,
    software.data,
    storageServices.data,
  ]);
}
